import { FCC } from "@/types/common";
import { TGetCompanyDto } from "@/api/services/main/company/dtos/get-company.dto";
import { Grid, Typography } from "@mui/material";
import { ItemInfo } from "@/components/commons/ItemInfo";
import { InnerBox } from "@/components/elements/InnerBox";
import { CompanyFormTitle } from "@/features/company/components/company-info/CompanyFormTitle";
import { text } from "@/theme/colors";
import { useInitSetup } from "@/providers/InitSetupProvider";
import { USER_TYPE } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { DATE_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { useGetCompanySpecificIndustryList } from "@/api/services/global/master";

type TCompanyBasicInfoProps = {
  defaultValues: Partial<TGetCompanyDto>;
};

export const CompanyBasicInfo: FCC<TCompanyBasicInfoProps> = ({ defaultValues }) => {
  const { userType } = useInitSetup();
  const isCompanyUser = userType === USER_TYPE.COMPANY_USER;
  const { getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry } = useGetCompanySpecificIndustryList();

  // Resolve 業務区分 display names from the cached master list (the API returns only their ids).
  const getBusinessClassificationNames = (
    industryId?: number | null,
    businessClassificationIds?: string[] | null,
  ) => {
    if (!industryId || !businessClassificationIds?.length) return undefined;
    const industryOptions = getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry(industryId);
    const names = businessClassificationIds
      .map((id) => industryOptions.find((opt) => Number(opt.value) === Number(id))?.label)
      .filter((label): label is string => Boolean(label));
    return names.length ? names.join("、") : undefined;
  };

  const startDate = defaultValues?.startDate
    ? dayjs(defaultValues?.startDate).format(DATE_FORMAT)
    : EMPTY_STR.DATE;
  const endDate = defaultValues?.endDate
    ? dayjs(defaultValues?.endDate).format(DATE_FORMAT)
    : EMPTY_STR.DATE;

  return (
    <InnerBox title={<CompanyFormTitle text="基本情報" />} showDivider={false}>
      <Grid container rowSpacing={2} columnSpacing={3}>
        <Grid item xs={6}>
          <ItemInfo label="管理番号" value={defaultValues?.managementNumber} />
        </Grid>
        <Grid item xs={6}></Grid>
        <Grid item xs={6}>
          <ItemInfo label="会社・法人名" value={defaultValues?.companyName} required />
        </Grid>
        <Grid item xs={6}>
          <ItemInfo label="会社・法人名（ふりがな）" value={defaultValues?.companyKanaName} />
        </Grid>
         <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <ItemInfo label="特定産業分野１" value={defaultValues?.companySpecificIndustry1?.companySpecificIndustry} required />
              </Grid>
              <Grid item xs={6}>
                <ItemInfo label="業務区分１" value={getBusinessClassificationNames(defaultValues?.companySpecificIndustryId1, defaultValues?.companySpecificBusinessClassificationIds1)} />
              </Grid>

              <Grid item xs={6}>
                <ItemInfo label="特定産業分野２" value={defaultValues?.companySpecificIndustry2?.companySpecificIndustry} />
              </Grid>
              <Grid item xs={6}>
                <ItemInfo label="業務区分２" value={getBusinessClassificationNames(defaultValues?.companySpecificIndustryId2, defaultValues?.companySpecificBusinessClassificationIds2)} />
              </Grid>

              <Grid item xs={6}>
                <ItemInfo label="特定産業分野３" value={defaultValues?.companySpecificIndustry3?.companySpecificIndustry} isShowDivider={!isCompanyUser} />
              </Grid>
              <Grid item xs={6}>
                <ItemInfo label="業務区分３" value={getBusinessClassificationNames(defaultValues?.companySpecificIndustryId3, defaultValues?.companySpecificBusinessClassificationIds3)} isShowDivider={!isCompanyUser} />
              </Grid>
            </Grid>
          </Grid>

          {!isCompanyUser && (
            <>
              <Grid item xs={6}>
                <ItemInfo label="利用開始日" value={startDate} required />
              </Grid>
              <Grid item xs={6}></Grid>
              <Grid item xs={6}>
                <ItemInfo label="利用終了日" value={endDate} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body14Light" color={text.secondary}>注意：利用終了日以降は所属機関および学習者はログインができなくなります。</Typography>
              </Grid>
            </>
          )}
      </Grid>
    </InnerBox>
  );
};
